import { ArrowLeftIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams } from 'react-router';
import type { AreaContext } from '../../components/layout/AppShell';
import { TemplateMetadataForm } from '../../components/studio/TemplateMetadataForm';
import { TokenList } from '../../components/studio/TokenList';
import { showConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { ApiError, describeApiError } from '../../data/http';
import { getTemplate, listCategories } from '../../data/templates';
import { listTemplateVersions, uploadTemplateVersion } from '../../data/versions';
import type { CategorySummary, ConfirmationRequired, TemplateDetail, TemplateVersionSummary } from '../../data/types';

export function TemplateDetailPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { templateId } = useParams();
  const { roles } = useOutletContext<AreaContext>();
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [currentVersion, setCurrentVersion] = useState<TemplateVersionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    if (!templateId) return Promise.resolve();
    return Promise.all([getTemplate(templateId), listCategories(), listTemplateVersions(templateId)])
      .then(([result, cats, versions]) => {
        setDetail(result);
        setCategories(cats);
        setCurrentVersion(versions.find((version) => version.isCurrent) ?? null);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t('errors.loadTemplates'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [templateId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Retries with confirmRemoval once the author accepts the confirm-removal modal. */
  const refreshFromLibrary = async (confirmRemoval = false) => {
    if (!detail) return;
    setRefreshing(true);
    try {
      const created = await uploadTemplateVersion(detail.template.id, {
        libraryItemId: detail.template.libraryItemId,
        confirmRemoval
      });
      toast({
        title: created.unchanged
          ? t('studio.detail.noSharePointChanges')
          : t('studio.detail.refreshed', { version: created.version }),
        type: created.unchanged ? 'info' : 'success'
      });
      await load();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as ConfirmationRequired;
        const confirmed = await showConfirmModal({
          title: t('studio.versions.confirmRemovalTitle'),
          description: t('studio.versions.confirmRemovalDescription', { tokens: body.diff.removedTokens.join(', ') }),
          variant: 'danger'
        });
        if (confirmed) await refreshFromLibrary(true);
      } else {
        toast({ title: describeApiError(err, t('errors.uploadVersion')), type: 'error' });
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <Link
        to="/studio/templates"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <ArrowLeftIcon className="size-4" aria-hidden="true" />
        {t('studio.templates.title')}
      </Link>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {detail ? (
        <>
          <h1 className="mt-3 text-3xl font-heading font-semibold tracking-normal text-foreground">
            {detail.template.name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{detail.template.description}</p>

          <div className="mt-3 flex flex-wrap gap-3">
            <Link
              to={`/studio/templates/${detail.template.id}/versions`}
              className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <ClockIcon className="size-4" aria-hidden="true" />
              {t('studio.versions.link')}
            </Link>

            {detail.template.source === 'library' ? (
              <button
                type="button"
                onClick={() => void refreshFromLibrary()}
                disabled={refreshing}
                className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ArrowPathIcon className={`size-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
                {refreshing ? t('studio.detail.refreshing') : t('studio.detail.refreshFromLibrary')}
              </button>
            ) : null}

            {detail.template.libraryWebUrl ? (
              <a
                href={detail.template.libraryWebUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <ArrowTopRightOnSquareIcon className="size-4" aria-hidden="true" />
                {t('studio.detail.openInSharePoint')}
              </a>
            ) : null}
          </div>

          <dl className="mt-6 grid gap-4 rounded-lg border border-border bg-surface p-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('studio.templates.columns.type')}
              </dt>
              <dd className="mt-1 text-sm uppercase text-foreground">{detail.template.documentType}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('studio.detail.code')}
              </dt>
              <dd className="mt-1 text-sm text-foreground">{detail.template.code || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('studio.detail.country')}
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {detail.template.allCountries ? t('studio.detail.allCountries') : detail.template.country || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('studio.detail.language')}
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {detail.template.allLanguages ? t('studio.detail.allLanguages') : detail.template.language || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('studio.detail.source')}
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {detail.template.source === 'library'
                  ? t('studio.detail.sourceLibrary', { name: detail.template.libraryItemName })
                  : t('studio.detail.sourceUpload')}
              </dd>
            </div>
          </dl>

          <TemplateMetadataForm
            template={detail.template}
            categories={categories}
            hasCompanyField={detail.properties.some((property) => property.propertyType === 'Company')}
            canDelete={roles.includes('admin')}
            onSaved={() => void load()}
          />

          <h2 className="mt-8 text-xl font-heading font-semibold text-foreground">
            {t('studio.detail.discoveredTokens')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('studio.detail.discoveredTokensHint')}</p>

          {currentVersion && (currentVersion.addedTokens.length > 0 || currentVersion.removedTokens.length > 0) ? (
            <div className="mt-3 space-y-1.5 rounded-sm border border-border bg-muted/40 p-4">
              <p className="text-xs font-medium text-muted-foreground">
                {t('studio.detail.recentChanges', { version: currentVersion.version })}
              </p>
              <TokenList label={t('studio.versions.added')} tokens={currentVersion.addedTokens} tone="added" />
              <TokenList label={t('studio.versions.removed')} tokens={currentVersion.removedTokens} tone="removed" />
            </div>
          ) : null}

          <Link
            to={`/studio/templates/${detail.template.id}/properties`}
            className="mt-3 inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {t('properties.edit')}
          </Link>

          {detail.properties.length === 0 ? (
            <p className="mt-4 rounded-lg border border-dashed border-input bg-muted p-6 text-center text-sm text-muted-foreground">
              {t('studio.detail.noTokens')}
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-surface">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.detail.token')}</th>
                    <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.detail.inferredType')}</th>
                    <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('studio.detail.required')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {detail.properties.map((property) => (
                    <tr key={property.tokenName} className="hover:bg-muted">
                      <td className="px-4 py-3 font-mono text-foreground">
                        <span className="inline-flex items-center gap-2">
                          {property.tokenName}
                          {currentVersion?.addedTokens.includes(property.tokenName) ? (
                            <span className="inline-flex items-center rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800 ring-1 ring-inset ring-sky-200">
                              {t('studio.detail.newToken')}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{property.propertyType}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {property.isRequired ? t('common.yes') : t('common.no')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
